import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken, getUserId } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const fetchSecurityQuestionsAsync = createAsyncThunk(
  "settings/fetchSecurityQuestions",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/recoveryQuestion`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("response.data in fetchSecurityQuestionsAsync", response.data);

      return response.data.data; // Extract the questions array from the response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Thunk to fetch user's recovery questions
export const fetchUserRecoveryQuestionsAsync = createAsyncThunk(
  "settings/fetchUserRecoveryQuestions",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/auth/get-recovery-question`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("response.data in fetchUserRecoveryQuestionsAsync", response.data);

      return response.data.data || []; // Extract the user's recovery questions
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add security question + answer in a single request.
// The backend expects an array format: [{ question: questionId, answer, userID }]
export const addSecurityQuestionAsync = createAsyncThunk(
  "settings/addSecurityQuestion",
  async ({ questionId, answer }, { rejectWithValue, dispatch }) => {
    console.log("questionId in addSecurityQuestionAsync", questionId);
    console.log("answer in addSecurityQuestionAsync", answer);
    try {
      const token = getAccessToken();
      const userID = getUserId();

      if (!token) {
        throw new Error("No authentication token found");
      }

      if (!userID) {
        throw new Error("No user ID found");
      }

      // Backend expects an array with object containing question, answer, and userID
      const requestBody = [
        {
          question: questionId,
          answer: answer,
          userID: userID,
        },
      ];

      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/add-recovery-question`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("response.data in addSecurityQuestionAsync", response.data);

      // Refresh the user's recovery questions after adding + answering
      await dispatch(fetchUserRecoveryQuestionsAsync());

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add answer security question thunk
export const answerSecurityQuestionAsync = createAsyncThunk(
  "settings/answerSecurityQuestion",
  async ({ _id, answer }, { rejectWithValue, dispatch }) => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/answer-recovery-question`,
        { _id, answer },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Refresh the user's recovery questions after updating an answer
      await dispatch(fetchUserRecoveryQuestionsAsync());

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  isLoading: false,
  error: null,
  success: false,
  securityQuestions: [], // Available security questions
  userRecoveryQuestions: [], // User's recovery questions
};

// Settings slice
const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearSettingsState: (state) => {
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add security question cases
      .addCase(addSecurityQuestionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addSecurityQuestionAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(addSecurityQuestionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(fetchSecurityQuestionsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSecurityQuestionsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.securityQuestions = action.payload;
        state.error = null;
      })
      .addCase(fetchSecurityQuestionsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserRecoveryQuestionsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserRecoveryQuestionsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userRecoveryQuestions = action.payload;
        state.error = null;
      })
      .addCase(fetchUserRecoveryQuestionsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(answerSecurityQuestionAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(answerSecurityQuestionAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(answerSecurityQuestionAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload;
      });
  },
});

// Actions
export const { clearSettingsState } = settingsSlice.actions;

// Selectors
export const selectSettingsLoading = (state) => state.settings.isLoading;
export const selectSettingsError = (state) => state.settings.error;
export const selectSettingsSuccess = (state) => state.settings.success;
export const selectSecurityQuestions = (state) =>
  state.settings.securityQuestions;
export const selectUserRecoveryQuestions = (state) =>
  state.settings.userRecoveryQuestions;

export default settingsSlice.reducer;
